import { Button, IconButton, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';

import { planApiKeyStore } from '../../../../api/plan-api-key-store';
import { GuardedComponent } from '../../../../components/guarded-component';
import { SiTrackGrid } from '../../../../components/si-track-grid/si-track-grid';
import { UserPermissionName } from '../../../../models/user-permission-name';
import { planColumns } from './grid-columns';
import { useEffect, useState } from 'react';
import { ProjectPlanDialog } from '../../project-plan/project-plan-dialog.component';
import { useLoadData } from '../../../../hooks/useLoadData';
import { ApiResponse } from '../../../../models/api-response';
import { useFileDownload } from '../../../../components/file-download';
import { ConfirmDeletePlanDialog } from '../../../plan-library/confirm-delete-plan.dialog';
import { State } from '@progress/kendo-data-query';
import useDebounce from '../../../../hooks/use-debounce';
import { gridApiKeyStore } from '../../../../api/grid-api-key-store';
import { GridColumnConfig, GridPreference } from '../../../../models/gridCoumnConfig';
import { useSaveData } from '../../../../hooks/useSaveData';
import { PlanStatusDialog } from '../../../plan-library/plan-status.dialog';
import { useAuthorize } from '../../../../hooks/use-authorize';

interface CellProps {
  planData: any;
}

interface CustomGridCellProps {
  field: string;
}

interface PlanRequestArgs {
  customerID?: number;
  projectID?: number;
  officeID?: number;
}

interface PlanTab {
  planRequestArgs: PlanRequestArgs;
}

export const PlanTab = ({ planRequestArgs }: PlanTab) => {
  const [planID, setPlanID] = useState<any>();
  const [editItem, setEditItem] = useState<Partial<any>>();
  const [openDeleteDialog, setDeleteDialog] = useState<boolean>(false);
  const [planStatusValue, setPlanStatusValue] = useState<any>();

  const { downloadFile, isLoading: isFileDownloadLoading } = useFileDownload();

  const { data, isLoading, isFetching, refetch } = useLoadData<ApiResponse<any[]>>(
    planApiKeyStore.getPlans({
      // officeID: planRequestArgs.officeID,
      projectID: planRequestArgs.projectID,
    })
  );

  const handleEditPlan = (planData: any) => {
    setEditItem(planData);
  };
  const handleEditClose = () => {
    setEditItem(undefined);
  };
  const handleCreatePlan = () => {
    setEditItem({});
  };

  const handleDeleteClosed = () => {
    setDeleteDialog(false);
    setPlanID('');
  };

  const openConfirmDialog = (planID: any) => {
    setPlanID(planID);
    setDeleteDialog(true);
  };

  const handleDownloadFile = async (data: any) => {
    const value = {
      planID: Number(data?.planID) || 0,
      fileName: data?.fileName,
      fileType: data?.fileType,
    };
    downloadFile({ additionalData: value });
  };

  const handleStatusChange = (planID: any, status: any) => {
    setPlanStatusValue({ planID: planID, isActive: status });
  };

  const FileDownloadCell = ({ planData }: CellProps) => {
    const fileURL = planData?.fileURL;

    return (
      <td>
        {fileURL ? (
          <GuardedComponent requiredPermissions={[UserPermissionName.DOWNLOAD_FILE]}>
            <IconButton style={{ padding: 5 }} aria-label="view" onClick={() => handleDownloadFile(planData)}>
              <Tooltip title={<Typography variant="body1">Download</Typography>}>
                <DownloadIcon className="btnEdit" />
              </Tooltip>
            </IconButton>
          </GuardedComponent>
        ) : (
          <Typography variant="body1">No file uploaded</Typography>
        )}
      </td>
    );
  };

  const isEditPlanPermission = useAuthorize([UserPermissionName?.EDIT_PLAN]);

  const PlanStatusCell = ({ planData }: CellProps) => {
    const status = planData?.planStatus;
    const [isHovered, setIsHovered] = useState(false);
    const handleClickUnit = () => { handleStatusChange(planData?.planID, status) };

    return (
      <td>
        {isEditPlanPermission ?
          <Typography onClick={handleClickUnit}
            component="span"
            style={{
              color: isHovered ? '#0000b5' : '#0000b5',
              textDecoration: isHovered ? 'underline' : 'none',
              cursor: 'pointer',
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {status ? "Active" : "Inactive"}
          </Typography>
          :
          <Typography component="span">{status ? "Active" : "Inactive"}</Typography>
        }
      </td >
    );
  };

  const CustomGridCell = ({ field }: CustomGridCellProps) => {
    if (field === 'fileName') {
      return (dataItem: any) => {
        return <FileDownloadCell planData={dataItem.dataItem} />;
      };
    }
    if (field === 'planStatus') {
      return (dataItem: any) => {
        return <PlanStatusCell planData={dataItem.dataItem} />;
      };
    }
  };

  const downloadElement = (dataItem: any) => {
    return (
      <GuardedComponent requiredPermissions={[UserPermissionName.DELETE_PLAN]}>
        <IconButton
          style={{ padding: 5 }}
          aria-label="view"
          onClick={() => openConfirmDialog(dataItem?.dataItem?.planID)}
        >
          <Tooltip title={<Typography variant="body1">Delete</Typography>}>
            <DeleteIcon className="btnDelete" />
          </Tooltip>
        </IconButton>
      </GuardedComponent>
    );
  };

  // -------------------------------------------------Grid Layout------------------------------

  const {
    isLoading: isLoadingGridLayout,
    data: gridLayouts,
    refetch: refetchGridLayout,
  } = useLoadData<ApiResponse<GridPreference>>(gridApiKeyStore.getGridLayout('Project Details', 'Plan'));

  const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
  const [gridConfig, setGridConfig] = useState<{
    dataState: State | undefined;
    columns: any[] | undefined;
  }>();

  const [filteredPlans, setFilteredPlans] = useState<any[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    const filteredPlanList = data?.data.filter((plan) =>
      Object.values(plan).some(
        (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      )
    );
    setFilteredPlans(filteredPlanList || []);
  }, [debouncedSearchTerm, data?.data]);

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
    const data = {
      filterJSON: JSON.stringify(gridDataState),
      columnJSON: JSON.stringify(gridColumns),
      gridName: 'Plan',
      pageName: 'Project Details',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  const handleResetLayout = () => {
    const data = {
      filterJSON: null,
      columnJSON: null,
      gridName: 'Plan',
      pageName: 'Project Details',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  useEffect(() => {
    if (!gridLayouts?.data) return;

    try {
      const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

      const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

      //   if (filterData && columnJson) {
      setGridConfig({
        columns: columnJson,
        dataState: filterData,
      });
      //   }
    } catch (error) {
      console.log('Error parsing grid layout JSON:', error);
    }
  }, [gridLayouts?.data]);

  return (
    <div className="borderTop">
      <GuardedComponent requiredPermissions={[UserPermissionName.ADD_PLAN]}>
        <Button
          className="innerTab-button"
          size="small"
          variant="contained"
          disableElevation={true}
          startIcon={<AddIcon />}
          onClick={handleCreatePlan}
        >
          Add Plan / File
        </Button>
      </GuardedComponent>
      <SiTrackGrid
        fileTitle='project_plan'
        // dataItems={data?.data}
        columns={planColumns}
        dataItemKey="planID"
        customCell={(field) =>
          CustomGridCell({
            field,
          })
        }
        onEdit={handleEditPlan}
        CustomComponent={downloadElement}
        actionPermissions={{
          edit: UserPermissionName.EDIT_PLAN,
          export: UserPermissionName.DOWNLOAD_GRID,
        }}
        isBusy={isLoading || isFetching || isFileDownloadLoading}
        dataItems={filteredPlans || []}
        reorderable
        handleSearch={handleInputChange}
        handleSaveLayout={handleSaveLayout}
        isSaveLayout={true}
        gridConfigFromApi={gridConfig}
        handleResetLayout={handleResetLayout}
      // -----------------Grid Layout-------
      />
      {editItem && (
        <ProjectPlanDialog
          editPlan={editItem}
          planData={data?.data ?? []}
          planRequestArgs={planRequestArgs}
          onClosed={handleEditClose}
        />
      )}
      {openDeleteDialog && (
        <ConfirmDeletePlanDialog
          planRequestArgs={{ planID: planID, projectID: planRequestArgs?.projectID }}
          isPlanLoading={isLoading}
          refetching={refetch}
          onClosed={handleDeleteClosed}
        />
      )}
      {planStatusValue && (
        <PlanStatusDialog
          statusValue={planStatusValue}
          isPlanLoading={isLoading || isFetching}
          refetching={refetch}
          onClosed={() => setPlanStatusValue(undefined)} />
      )}
    </div>
  );
};
